<template>
  <!-- Resolve path first — no method-card flash for FEATURE_DISABLED / ALLOW_UNENROLLED -->
  <div :class="$style.formContainer">
    <LoginCard
      v-if="!isAdmin() && !pathResolved"
      key="resolving"
      header="Reset User Password"
    >
      <div :class="$style.resolvingContainer">
        <EmailBadge :email="userEmail">
          <template slot="badgeAction">
            <button
              :class="$style.actionLink"
              type="button"
              data-test-id="badgeActionButton"
              @click="goToUserLogin"
            >
              Change
            </button>
          </template>
        </EmailBadge>
        <LoginActionButton
          :action="noop"
          :isLoading="true"
          type="button"
        >
          Continue
        </LoginActionButton>
      </div>
    </LoginCard>

    <transitionGroup
      v-else
      :enterClass="$style.fadeEnter"
      :enterActiveClass="$style.fadeEnterActive"
      :leaveActiveClass="$style.fadeLeaveActive"
      :leaveToClass="$style.fadeLeaveTo"
    >
      <UserResetPasswordMethodSelection
        v-if="!isAdmin() && showMethodSelection && !emailPathSelected"
        key="method"
        @goToLogin="goToUserLogin"
        @selectEmail="onSelectEmail"
        @selectMfa="onSelectMfa"
      />
      <UserResetPasswordRequest
        v-else-if="!isAdmin()"
        key="user"
        :errorMessage="errorMessage"
        :isLoading="isLoading"
        :success="success"
        @goToLogin="goToUserLogin"
        @submit="request"
      />
      <AdminResetPasswordRequest
        v-else
        key="admin"
        :errorMessage="errorMessage"
        :isLoading="isLoading"
        :success="success"
        @goToLogin="goToAdminLogin"
        @submit="request"
      />
    </transitionGroup>
    <LogoIcon />
  </div>
</template>

<script>
import {
  clearPwdResetSessionEmail,
  syncPwdResetSessionEmail,
} from 'client/util/pwdResetSessionEmail';
import { mapStores } from 'pinia';
import AdminResetPasswordRequest from 'client/containers/admin/PasswordReset/AdminResetPasswordRequest.vue';
import EmailBadge from 'client/components/EmailBadge.vue';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginCard from 'client/components/LoginCard.vue';
import UserLoginStore from 'client/stores/UserLoginStore';
import UserResetPasswordMethodSelection from 'client/containers/user/UserResetPassword/UserResetPasswordMethodSelection.vue';
import UserResetPasswordRequest from 'client/containers/user/UserResetPassword/UserResetPasswordRequest.vue';
import api from 'client/api';
import loginUtil from 'client/components/mixins/loginUtil';
import routerService from 'client/services/routerService';
import userResetPasswordService from 'client/services/userResetPasswordService';

const REASON_ALLOW_UNENROLLED = 'ALLOW_UNENROLLED';
const REASON_FEATURE_DISABLED = 'FEATURE_DISABLED';

export default {
  name: 'RequestPasswordReset',

  components: {
    AdminResetPasswordRequest,
    EmailBadge,
    LoginActionButton,
    LoginCard,
    UserResetPasswordMethodSelection,
    UserResetPasswordRequest,
  },

  mixins: [loginUtil],

  props: {
    clientType: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      default: undefined,
    },
    redirectTo: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    success: false,
    emailPathSelected: false,
    pathResolved: false,
    showMethodSelection: false,
  }),

  computed: {
    ...mapStores(UserLoginStore),

    userEmail() {
      return this.userLoginStore.email || '';
    },
  },

  created() {
    // Admin forgot-password is unchanged (no MFA-direct chooser)
    if (this.isAdmin()) {
      this.pathResolved = true;
      this.showMethodSelection = false;
    }
  },

  async mounted() {
    if (this.pathResolved) {
      return;
    }
    await this.resolveResetPath();
  },

  methods: {
    noop() {},

    async resolveResetPath() {
      // NA-4597: restore email after refresh so method selection (email vs MFA) still appears
      const email = syncPwdResetSessionEmail(
        () => this.userLoginStore.email || '',
        (value) => { this.userLoginStore.email = value; },
      );
      if (!email) {
        // Cannot probe SI without email — email-only (no MFA-direct chooser)
        this.showMethodSelection = false;
        this.pathResolved = true;
        return;
      }

      try {
        // SI Split flag + allowUnenrolled are authoritative (no client FF gate)
        const result = await userResetPasswordService.checkMfaEligibility({ email });
        this.userLoginStore.eligibility = result;
        // FEATURE_DISABLED / ALLOW_UNENROLLED → email-only (no method-card flash)
        this.showMethodSelection = result.reason !== REASON_FEATURE_DISABLED
          && result.reason !== REASON_ALLOW_UNENROLLED;
      } catch {
        this.userLoginStore.eligibility = { eligible: false };
        // API failure: email-only (stable, matches frozen email path)
        this.showMethodSelection = false;
      }

      this.pathResolved = true;
    },

    goToAdminLogin() {
      routerService.goToAdminLogin();
    },

    // Preserve SSO/oauth return context on login and MFA-direct navigation
    ssoQueryParams() {
      return this.context && this.redirectTo
        ? { context: this.context, redirectTo: this.redirectTo }
        : {};
    },

    goToUserLogin() {
      clearPwdResetSessionEmail();
      routerService.goToUserLogin({ query: this.ssoQueryParams() });
    },

    onSelectEmail() {
      this.emailPathSelected = true;
    },

    onSelectMfa() {
      routerService.goToUserMfaDirectPasswordReset({ query: this.ssoQueryParams() });
    },

    request(email) {
      this.isLoading = true;
      this.errorMessage = '';
      api[this.clientType].requestPasswordReset({ email })
        .then(() => {
          this.isLoading = false;
          this.success = true;
        }).catch(() => {
          this.isLoading = false;
          this.errorMessage = 'There was an error handling your request.';
        });
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';
@value (jcInformational900) from 'css/brand-colors.css';

.resolvingContainer {
  padding: 0 1rem;
}

.actionLink {
  background-color: transparent;
  border: none;
  color: jcInformational900;
  cursor: pointer;
  margin-left: 1rem;
  padding: 0;
  text-decoration: none;
}

.fadeEnterActive {
  transition: opacity 0.35s 0.35s;
}

.fadeLeaveActive {
  transition: opacity 0.35s;
}

.fadeEnter,
.fadeLeaveTo {
  opacity: 0;
}
</style>
